import { useState } from "react";

interface Props {
  images: string[];
  title: string;
  imageFilter?: string;
}

function GalleryImage({ src, alt, imgError, imageFilter, onError }: { src: string; alt: string; imgError: boolean; imageFilter?: string; onError: () => void }) {
  if (imgError) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-foreground-400 bg-gradient-to-br from-background-200 via-background-150 to-background-200">
        <i className="ri-image-line text-4xl mb-2"></i>
        <span className="text-xs">{alt}</span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-cover object-top"
      style={imageFilter ? { filter: imageFilter } : undefined}
      onError={onError}
    />
  );
}

export default function ProductGallery({ images, title, imageFilter }: Props) {
  const [active, setActive] = useState(0);
  const [imgErrors, setImgErrors] = useState<Record<number, boolean>>({});

  const handleImgError = (index: number) => {
    setImgErrors((prev) => ({ ...prev, [index]: true }));
  };

  return (
    <div className="w-full">
      <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden bg-background-200 border border-background-200/70">
        <GalleryImage
          src={images[active]}
          alt={title}
          imgError={!!imgErrors[active]}
          imageFilter={imageFilter}
          onError={() => handleImgError(active)}
        />
        {images.length > 1 && (
          <>
            <button
              onClick={() => setActive((active - 1 + images.length) % images.length)}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background-50/90 hover:bg-background-50 flex items-center justify-center text-foreground-900 cursor-pointer"
              aria-label="Previous"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              onClick={() => setActive((active + 1) % images.length)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background-50/90 hover:bg-background-50 flex items-center justify-center text-foreground-900 cursor-pointer"
              aria-label="Next"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </>
        )}
      </div>

      <div className="mt-3 grid grid-cols-5 gap-2">
        {images.slice(0, 5).map((img, i) => (
          <button
            key={img + i}
            onClick={() => setActive(i)}
            className={`aspect-square rounded-md overflow-hidden border-2 transition-all cursor-pointer bg-gradient-to-br from-background-200 via-background-150 to-background-200 ${
              active === i ? "border-primary-500" : "border-transparent hover:border-background-300"
            }`}
          >
            {imgErrors[i] ? (
              <div className="w-full h-full flex items-center justify-center text-foreground-400">
                <i className="ri-image-line text-lg"></i>
              </div>
            ) : (
              <img
                src={img}
                alt=""
                className="w-full h-full object-cover object-top"
                style={imageFilter ? { filter: imageFilter } : undefined}
                onError={() => handleImgError(i)}
              />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}